import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box } from "@mui/material";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import { isEqualSimple } from "../utils/basicFunctions";

import { TreeViewMenuModalPlayer } from "../Components/TreeView/TreeViewMenuModalPlayer";
import { TreeViewMenuModalJob } from "../Components/TreeView/TreeViewMenuModalJob";
import { HomeIcon, LogoIcon, JobIcon, JobIconStar, JobIconArchive } from "../CustomIcons/CustomIcons";
import { useAuthController, useDataSource, useFireCMSContext } from "@camberi/firecms";
import { useLocation, Link } from "react-router-dom";
import { ReferenceType, ActionType } from "../Types/types";
import { ConfirmationServiceProvider } from "../Components/TreeView/Components/ConfirmationService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";

const drawerWidth = 240;

function useQuery() {
	const { search } = useLocation();

	return React.useMemo(() => new URLSearchParams(search), [search]);
}

const createMobileHeaderCenter = (text: string, icon: any) => {
	return (
		<Box sx={{ width: "100%", pr: 1, pl: 0.5, pt: 1, pb: 1, borderRadius: "5px", display: "flex", alignItems: "center", justifyItems: "center", backgroundColor: "secondary.main" }}>
			<Box component={icon} sx={{ mr: 1 }} />
			<Typography sx={{ mr: 1, overflow: "hidden", textOverflow: "ellipsis" }} variant="body2">
				{text}
			</Typography>
		</Box>
	);
};

const JobListItem = (props: any) => {
	const [hover, setHover] = useState<boolean>(false);
	const { userIsAdmin } = props;

	const job_has_setup = Object.keys(props.job?.values.roles?.details || {}).length > 0;

	return (
		<ListItem onMouseMove={() => setHover(true)} onMouseLeave={() => setHover(false)} key={props.job.id + "_li"} disablePadding>
			<ListItemButton
				onClick={() => {
					props.createAndSetMobileNavCenter(props?.job?.values?.name.replace(`_${props?.job?.values?.client}_`, "_"), JobIcon);
				}}
				component={Link}
				to={`/jobs/${props.job.id}/${job_has_setup ? "versions" : "topology"}`}
				disableRipple
			>
				{props?.job?.values?.job_archive_overwrite === "pin" ? <JobIconStar sx={{ mr: 0.5 }} /> : props?.job?.values?.job_archive_overwrite === "force_archive" ? <JobIconArchive sx={{ mr: 0.5 }} /> : <JobIcon sx={{ mr: 0.5 }} />}

				<ListItemText
					primary={
						<Typography sx={{ width: 160, overflow: "hidden", textOverflow: "ellipsis" }} variant="body2">
							{props?.job?.values?.name.replace(`_${props?.job?.values?.client}_`, "_")}
						</Typography>
					}
				/>

				{(userIsAdmin && hover) || (userIsAdmin && props.mobileOpen) ? (
					<IconButton
						size="small"
						onClick={() => {
							props.jobDialogHandleOpen(props.job.id, "edit");
						}}
					>
						<MoreVertIcon fontSize="inherit" />
					</IconButton>
				) : null}
			</ListItemButton>
		</ListItem>
	);
};

export function NavDrawer({ jobsCollection, usersCollection }: any) {
	const jobDialogRef = useRef<any>();
	const playerDialogRef = useRef<any>();
	const theme = useTheme();
	const matches = useMediaQuery(theme.breakpoints.down(1920));
	const [authUser, setAuthUser] = useState<any>();
	const [jobs, setJobs] = useState<any[]>([]);
	const [groupedJobs, setGroupedJobs] = useState<any>({});
	const [users, setUsers] = useState<any[]>([]);
	const [userIsAdmin, setUserIsAdmin] = useState<boolean>(false);
	const [userDataListenerInit, setUserDataListenerInit] = useState<void>(undefined);
	const [userJobsListenerInit, setUserJobsListenerInit] = useState<void>(undefined);
	const [adminUsersListenerInit, setAdminUsersListenerInit] = useState<void>(undefined);

	const dataSource = useDataSource();
	const authController = useAuthController();
	const context = useFireCMSContext();

	const [mobileOpen, setMobileOpen] = React.useState(false);
	const [adminOpen, setAdminOpen] = React.useState(false);
	const [archiveOpen, setArchiveOpen] = React.useState(false);
	const [userData, setUserData] = useState<any>();

	const [mobileNavCenter, setMobileNavCenter] = useState<any>(<CircularProgress sx={{ p: 1 }} color="secondary" />);

	useEffect(() => {
		if (authController?.user?.uid) {
			if (!isEqualSimple(authUser, authController?.user)) {
				setAuthUser(authController.user);
			}
		}
	}, [authController]);

	const onUserDataUpdate = (evt: any) => {
		if (!isEqualSimple(evt[0], userData)) {
			if (evt[0].values?.role === "admin") setUserIsAdmin(true);
			setUserData(evt[0]);
		}
	};

	useEffect(() => {
		if (authUser?.uid) {
			if (typeof userDataListenerInit === "undefined" && usersCollection && dataSource.listenCollection) {
				const userDataListener = dataSource.listenCollection({
					path: usersCollection.path,
					collection: usersCollection,
					filter: { __name__: ["==", authUser.uid] },
					onUpdate: onUserDataUpdate,
				});
				setUserDataListenerInit(() => () => userDataListener);
			}
		}
	}, [dataSource, usersCollection, authUser]);

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	let location = useLocation();
	React.useEffect(() => {}, [location]);

	let query = useQuery();

	useEffect(() => {
		if (userData?.values?.status === "active") {
			if (authUser?.uid) {
				if (typeof userJobsListenerInit === "undefined" && jobsCollection && dataSource.listenCollection) {
					const userJobListener = dataSource.listenCollection({
						path: jobsCollection.path,
						collection: jobsCollection,
						filter: !userIsAdmin ? { "roles.any": ["array-contains", authUser.uid] } : {},
						onUpdate: onJobsUpdate,
					});
					setUserJobsListenerInit(() => () => userJobListener);
				}
			}
		} else {
			setJobs([]);
			setGroupedJobs({ active: {}, archive: {} });
		}
	}, [dataSource, jobsCollection, authUser, userData]);

	const groupItemBy = function (array: any, property: any) {
		var hash: any = {},
			props: any = property.split(".");
		for (var i = 0; i < array.length; i++) {
			var key: any = props.reduce(function (acc: any, prop: any) {
				return acc && acc[prop];
			}, array[i]);
			if (!hash[key]) hash[key] = [];
			hash[key].push(array[i]);
		}
		return hash;
	};

	const onJobsUpdate = (jobs: any[]) => {
		const oneYearAgo = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000);

		const activeJobs: any[] = [];
		const archiveJobs: any[] = [];

		const sortJobs = (a: any, b: any) =>
			(b?.values?.job_archive_overwrite === "pin" || b?.values?.job_archive_overwrite === "force_archive" ? 1 : 0) - (a?.values?.job_archive_overwrite === "pin" || a?.values?.job_archive_overwrite === "force_archive" ? 1 : 0) ||
			(b?.values?.client === "stunning" ? 1 : 0) - (a?.values?.client === "stunning" ? 1 : 0) ||
			a?.values?.client.localeCompare(b?.values?.client) ||
			a?.values?.ident.localeCompare(b?.values?.ident);

		const isActiveJob = (job: any): boolean => {
			const overwrite = job?.values?.job_archive_overwrite ?? null;

			if (overwrite === "pin") return true;
			if (overwrite === "force_archive") return false;

			const lastModified = new Date(job?.values?.last_mod);
			const lastUpdate = job?.values?.last_update ? new Date(job.values.last_update) : null;

			const referenceDate = lastUpdate ?? lastModified;

			return referenceDate >= oneYearAgo;
		};

		const nextJobs = jobs.map((job) => {
			const active = isActiveJob(job);
			(active ? activeJobs : archiveJobs).push(job);
			return {
				...job,
			};
		});

		activeJobs.sort(sortJobs);
		archiveJobs.sort(sortJobs);

		setGroupedJobs({
			active: groupItemBy(activeJobs, "values.client"),
			archive: groupItemBy(archiveJobs, "values.client"),
		});

		setJobs(nextJobs);
	};

	const onAdminUsersUpdate = (evt: any) => {
		if (!isEqualSimple(evt, users)) {
			setUsers(evt);
		}
	};

	useEffect(() => {
		if (userIsAdmin) {
			if (typeof adminUsersListenerInit === "undefined" && usersCollection && dataSource.listenCollection) {
				const adminUsersListener = dataSource.listenCollection({
					path: usersCollection.path,
					collection: usersCollection,
					onUpdate: onAdminUsersUpdate,
				});
				setAdminUsersListenerInit(() => () => adminUsersListener);
			}
		}
	}, [userIsAdmin, dataSource]);

	const createAndSetMobileNavCenter = (text: string, icon: any) => {
		setMobileNavCenter(createMobileHeaderCenter(text, icon));
		setMobileOpen(false);
	};

	useEffect(() => {
		const user_jobs = jobs || [];
		const user_data = userData || undefined;

		let all_collabs: any = [];

		if (userIsAdmin) {
			all_collabs =
				users
					?.filter((user) => (user.values?.status === "active" || user.values?.status === "created") && user.values?.role !== "service")
					?.map((user) => {
						return { id: user.id, name: user.values?.email };
					}) || [];
		} else {
			user_jobs.forEach((job) => {
				Object.entries(job.values?.roles?.details || {}).forEach(([key, value]: any) => {
					all_collabs.push({ id: key, name: value?.name });
				});
			});
		}

		const user_collaborators = Array.from(new Set(all_collabs.map(JSON.stringify))).map((user: any) => JSON.parse(user)) as ReferenceType[];

		if (!isEqualSimple(authController?.extra?.user_jobs, user_jobs) || !isEqualSimple(authController?.extra?.user_data, user_data) || !isEqualSimple(authController?.extra?.user_collaborators, user_collaborators)) {
			authController.setExtra({
				user_jobs,
				user_data,
				user_collaborators,
				playerDialogHandleOpen,
				apiUrl: "https://us-central1-stunning-boost.cloudfunctions.net/api",
				mobileNavHandleOpen: () => {
					setMobileOpen(true);
				},
				createAndSetMobileNavCenter,
			});
		}
	}, [userData, jobs, users]);

	const jobDialogHandleOpen = (originId: string, action: ActionType) => {
		jobDialogRef?.current?.openDialog(originId, action);
	};

	const formatQueryTimeSeconds = (value: any) => {
		const parsed = Number(value);

		if (!Number.isFinite(parsed) || parsed < 0) {
			return "";
		}

		const rounded = Math.round(parsed * 1000) / 1000;

		return rounded.toFixed(3).replace(/\.?0+$/, "");
	};

	const playerDialogHandleOpen = (assetId: string, versionId: string = "", fioAssetId: string = "", compareVersionId: string = "", comparePreviewAssetId: string = "", currentTimeSeconds: number | string | null = null, fromQuery: boolean = false) => {
		playerDialogRef?.current?.openDialog(
			assetId,
			versionId,
			fioAssetId,
			() => {
				if ("URLSearchParams" in window) {
					var newRelativePathQuery = window.location.pathname;

					window.history.replaceState(null, "", newRelativePathQuery);
				}
			},
			{
				compareVersionId,
				comparePreviewAssetId,
				currentTimeSeconds,
				fromQuery,
			}
		);

		if ("URLSearchParams" in window) {
			var searchParams = new URLSearchParams(window.location.search);

			searchParams.set("a", assetId);
			searchParams.set("v", versionId);
			searchParams.set("f", fioAssetId);

			if (compareVersionId?.length) {
				searchParams.set("cv", compareVersionId);
			} else {
				searchParams.delete("cv");
			}

			if (comparePreviewAssetId?.length) {
				searchParams.set("cf", comparePreviewAssetId);
			} else {
				searchParams.delete("cf");
			}

			const formattedTime = formatQueryTimeSeconds(currentTimeSeconds);

			if (formattedTime.length) {
				searchParams.set("t", formattedTime);
			} else {
				searchParams.delete("t");
			}

			var newRelativePathQuery = window.location.pathname + "?" + searchParams.toString();

			window.history.replaceState(null, "", newRelativePathQuery);
		}
	};

	const playerHandleClose = () => {
		playerDialogRef?.current?.closeDialog();
	};

	useEffect(() => {
		const assetId = query.get("a") || "";
		const versionId = query.get("v") || "";
		const fioAssetId = query.get("f") || "";
		const compareVersionId = query.get("cv") || "";
		const comparePreviewAssetId = query.get("cf") || "";
		const currentTimeSeconds = query.get("t");

		if (assetId?.length) {
			playerDialogHandleOpen(assetId, versionId, fioAssetId, compareVersionId, comparePreviewAssetId, currentTimeSeconds, true);
		} else {
			playerHandleClose();
		}
	}, [query, location]);

	const drawer = (
		<div>
			<ConfirmationServiceProvider>
				<TreeViewMenuModalPlayer ref={playerDialogRef} key={"PlayerDialog"} />
				<TreeViewMenuModalJob ref={jobDialogRef} key={"JobDialog"} jobs={jobs} />
			</ConfirmationServiceProvider>

			<Toolbar />

			<List sx={{ paddingBottom: "0px" }}>
				<ListItem disablePadding>
					<ListItemButton component={Link} to={`/`} disableRipple disabled>
						<ListItemText primary={<Typography variant="h6">{authUser?.displayName || ""}</Typography>} />
					</ListItemButton>
				</ListItem>

				<ListItem sx={{ paddingTop: "0px" }} disablePadding>
					<ListItemButton onClick={() => createAndSetMobileNavCenter("Dashboard", HomeIcon)} component={Link} to={`/`} disableRipple>
						<HomeIcon sx={{ mr: 0.5 }} />
						<ListItemText primary={<Typography variant="body2">{"Dashboard"}</Typography>} />
					</ListItemButton>
				</ListItem>
			</List>

			<List sx={{ p: 0, m: 0 }} dense={true}>
				<ListItem disablePadding disabled>
					<ListItemButton disableRipple>
						<KeyboardArrowDownRoundedIcon sx={{ mr: 0.5 }} />

						<ListItemText primary={<Typography variant="body2">{"Jobs"}</Typography>} />

						{!userIsAdmin ? null : (
							<IconButton
								size="small"
								onClick={() => {
									jobDialogHandleOpen("", "create");
								}}
							>
								<AddIcon fontSize="inherit" />
							</IconButton>
						)}
					</ListItemButton>
				</ListItem>

				{Object.keys(groupedJobs?.active || {}).map((client: any) => {
					const clientVals = groupedJobs?.active[client];

					return (
						<Box key={client} sx={{ mt: -1 }}>
							<List sx={{ paddingBottom: "0px", cursor: "pointer" }} dense={true}>
								<ListItem disablePadding>
									<ListItemButton disabled disableRipple>
										<Box sx={{ mr: 0.5 }} />

										<ListItemText
											primary={
												<Typography sx={{ textTransform: "capitalize" }} variant="body2">
													{client}
												</Typography>
											}
										/>
									</ListItemButton>
								</ListItem>
							</List>

							<Collapse in={true} timeout="auto" unmountOnExit>
								{clientVals.map((job: any) => {
									return (
										<JobListItem
											jobDialogHandleOpen={jobDialogHandleOpen}
											userIsAdmin={userIsAdmin}
											mobileOpen={mobileOpen}
											createAndSetMobileNavCenter={createAndSetMobileNavCenter}
											setMobileOpenClose={() => setMobileOpen(false)}
											key={job.id}
											job={job}
										/>
									);
								})}
							</Collapse>
						</Box>
					);
				})}
			</List>

			<Box>
				<List sx={{ paddingBottom: "0px", cursor: "pointer" }} dense={true}>
					<ListItem
						disablePadding
						onClick={() => {
							setArchiveOpen(!archiveOpen);
						}}
					>
						<ListItemButton disabled disableRipple>
							{archiveOpen ? <KeyboardArrowDownRoundedIcon sx={{ mr: 0.5 }} /> : <KeyboardArrowRightRoundedIcon sx={{ mr: 0.5 }} />}

							<ListItemText primary={<Typography variant="body2">{"Archive"}</Typography>} />
						</ListItemButton>
					</ListItem>
				</List>

				<Collapse in={archiveOpen} timeout="auto" unmountOnExit>
					<List sx={{ p: 0, m: 0 }} dense={true}>
						{Object.keys(groupedJobs?.archive || {}).map((client: any) => {
							const clientVals = groupedJobs?.archive[client];

							return (
								<Box key={client} sx={{ mt: -1 }}>
									<List sx={{ paddingBottom: "0px", cursor: "pointer" }} dense={true}>
										<ListItem disablePadding>
											<ListItemButton disabled disableRipple>
												<Box sx={{ mr: 0.5 }} />

												<ListItemText
													primary={
														<Typography sx={{ textTransform: "capitalize" }} variant="body2">
															{client}
														</Typography>
													}
												/>
											</ListItemButton>
										</ListItem>
									</List>

									<Collapse in={true} timeout="auto" unmountOnExit>
										{clientVals.map((job: any) => {
											return (
												<JobListItem
													jobDialogHandleOpen={jobDialogHandleOpen}
													userIsAdmin={userIsAdmin}
													mobileOpen={mobileOpen}
													createAndSetMobileNavCenter={createAndSetMobileNavCenter}
													setMobileOpenClose={() => setMobileOpen(false)}
													key={job.id}
													job={job}
												/>
											);
										})}
									</Collapse>
								</Box>
							);
						})}
					</List>
				</Collapse>
			</Box>

			{!userIsAdmin ? null : (
				<Box>
					<List sx={{ paddingBottom: "0px", cursor: "pointer" }} dense={true}>
						<ListItem
							disablePadding
							onClick={() => {
								setAdminOpen(!adminOpen);
							}}
						>
							<ListItemButton disabled disableRipple>
								{adminOpen ? <KeyboardArrowDownRoundedIcon sx={{ mr: 0.5 }} /> : <KeyboardArrowRightRoundedIcon sx={{ mr: 0.5 }} />}

								<ListItemText primary={<Typography variant="body2">{"Admin"}</Typography>} />
							</ListItemButton>
						</ListItem>
					</List>

					<Collapse in={adminOpen} timeout="auto" unmountOnExit>
						<List sx={{ p: 0, m: 0 }} dense={true}>
							{context.navigation.collections.map((collection) => {
								return (
									<ListItem key={collection.path} disablePadding>
										<ListItemButton onClick={() => setMobileOpen(false)} component={Link} to={`/c/${collection.path}`} disableRipple>
											<JobIcon sx={{ mr: 0.5, color: "rgba(150,0,0,1)" }} />

											<ListItemText primary={<Typography variant="body2">{collection.name}</Typography>} />
										</ListItemButton>
									</ListItem>
								);
							})}
						</List>
					</Collapse>
				</Box>
			)}
		</div>
	);

	const container = window !== undefined ? () => window.document.body : undefined;

	return (
		<Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }} aria-label="mailbox folders">
			<Box
				sx={{
					position: "absolute",
					top: 0,
					left: 0,
					width: "calc(100% - 20px)",
					height: "50px",
					display: { xs: "flex", sm: "none" },
					justifyContent: "center",
					mt: 1,
					ml: "10px",
					borderRadius: "15px",
					alignItems: "center",
					backgroundColor: "background.paper",
					zIndex: 1500,
				}}
			>
				<IconButton sx={{ flex: "1 1 0px", display: "flex", justifyContent: "start", alignItems: "start" }} size={"small"} disableRipple onClick={handleDrawerToggle}>
					{mobileOpen ? <CloseIcon sx={{ ml: 2 }} fontSize="inherit" /> : <ArrowBackIcon sx={{ ml: 2 }} fontSize="inherit" />}
				</IconButton>

				{mobileNavCenter !== null && !mobileOpen ? (
					<Box sx={{ display: "flex", maxWidth: "60%", overflow: "hidden" }}>{mobileNavCenter}</Box>
				) : (
					<IconButton sx={{ flex: "1 1 0px" }} color={"secondary"} size={"large"} disableRipple>
						<LogoIcon fontSize="inherit" />
					</IconButton>
				)}

				<IconButton sx={{ flex: "1 1 0px" }} color={"primary"} size={"large"} disableRipple></IconButton>
			</Box>

			<Drawer
				container={container}
				variant="temporary"
				open={mobileOpen}
				onClose={handleDrawerToggle}
				ModalProps={{
					keepMounted: true,
				}}
				sx={{
					display: { xs: "block", sm: "none" },
					"& .MuiDrawer-paper": { boxSizing: "border-box", width: "100%", display: "flex", alignItems: "center", backgroundImage: "none" },
				}}
			>
				{drawer}
			</Drawer>

			<Drawer
				variant="permanent"
				sx={{
					display: { xs: "none", sm: "block" },
					"& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
				}}
				open
			>
				{drawer}
			</Drawer>
		</Box>
	);
}
